import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "primereact/button";
import toast from "react-hot-toast";
import type {
  EventAdvancedConfigVisibilityRule,
  EventAdvancedConfigVisibilityRuleFormValues,
} from "../../../models/rulesEventAdvancedConfig";
import {
  useCreateEventAdvancedConfigVisibilityRuleMutation,
  useGetEventAdvancedConfigVisibilityRulesQuery,
  useToggleEventAdvancedConfigVisibilityRuleActiveMutation,
  useUpdateEventAdvancedConfigVisibilityRuleMutation,
} from "../../../store/services/rules-event-advanced-config";
import { useGetTenantsQuery } from "../../../store/services/tenants";
import { useTenantScope } from "../../../hooks/useTenantScope";
import {
  getAdminFacingApiErrorMessage,
  getApiErrorMessage,
  getApiErrorStatus,
  getApiValidationIssues,
} from "../../../utils/functions/api-errors";
import { eventAdvancedConfigTabConfig } from "../configs/eventAdvancedConfigTabConfig";
import {
  applyOwnedRulesTenantId,
  findGrupsTenantIdFromList,
  isRulesTenantOwnedByAdmin,
  matchesRulesTenantFilter,
  ownedRulesTenantIdForAdmin,
  resolveRulesTenantDisplayName,
} from "../constants/rulesTenant";
import type { AdminRulesCategoryPanelProps, AdminRulesFilterState } from "../types";
import { RulesDataTable } from "./RulesDataTable";
import { RulesInlineFormPanel, RulesInlineFormMode } from "./RulesInlineFormPanel";

const DUPLICATE_RULE_ERROR =
  "Ya existe una regla para esta combinación de tenant, tipo y subtipo.";

const RULES_LIST_LOAD_ERROR =
  "No se pudieron cargar las reglas de supresión de notificaciones y valoración.";

const getFormSubmitErrorMessage = (submitError: unknown, fallback: string): string => {
  const issues = getApiValidationIssues(submitError) ?? [];
  const issueMessage = issues
    .map((issue) => (typeof issue.message === "string" ? issue.message.trim() : ""))
    .find(Boolean);

  if (issueMessage) {
    return issueMessage;
  }

  return getApiErrorMessage(submitError) ?? fallback;
};

const isDuplicateRule = (
  rows: EventAdvancedConfigVisibilityRule[],
  values: EventAdvancedConfigVisibilityRuleFormValues,
  editingRowId: number | null
): boolean => {
  const formEventTypeId = values.eventTypeId ?? null;
  const formEventSubTypeId =
    formEventTypeId == null ? null : values.eventSubTypeId ?? null;

  return rows.some(
    (row) =>
      row.id !== editingRowId &&
      row.tenantId === values.tenantId &&
      (row.eventTypeId ?? null) === formEventTypeId &&
      (row.eventSubTypeId ?? null) === formEventSubTypeId
  );
};

export const EventAdvancedConfigRulesTabContent = ({
  createTrigger = 0,
  onFormVisibilityChange,
}: AdminRulesCategoryPanelProps) => {
  const [filters, setFilters] = useState<AdminRulesFilterState>({});
  const [formMode, setFormMode] = useState<RulesInlineFormMode>("hidden");
  const [editingRowId, setEditingRowId] = useState<number | null>(null);
  const [formValues, setFormValues] = useState<EventAdvancedConfigVisibilityRuleFormValues>(
    eventAdvancedConfigTabConfig.getEmptyFormValues()
  );
  const [formError, setFormError] = useState<string | null>(null);

  const { data, isLoading, isError, error, refetch } =
    useGetEventAdvancedConfigVisibilityRulesQuery({});
  const { data: tenants } = useGetTenantsQuery({});
  const tenantScope = useTenantScope();
  const ownedTenantId = ownedRulesTenantIdForAdmin(tenantScope.role, tenantScope.tenantId);

  const tenantNameById = useMemo(
    () =>
      new Map(
        (Array.isArray(tenants) ? tenants : []).map(
          (tenant) => [tenant.id, tenant.provider] as const
        )
      ),
    [tenants]
  );
  const grupsTenantId = useMemo(() => findGrupsTenantIdFromList(tenants), [tenants]);

  const allRows = useMemo(() => data ?? [], [data]);
  const rows = useMemo(
    () =>
      allRows
        .filter((row) => {
          if (
            filters.eventTypeId != null &&
            row.eventTypeId != null &&
            row.eventTypeId !== filters.eventTypeId
          ) {
            return false;
          }
          if (
            filters.eventSubTypeId != null &&
            row.eventSubTypeId != null &&
            row.eventSubTypeId !== filters.eventSubTypeId
          ) {
            return false;
          }
          if (!isRulesTenantOwnedByAdmin(row.tenantId, ownedTenantId)) {
            return false;
          }
          if (
            filters.tenantId != null &&
            !matchesRulesTenantFilter(row.tenantId, filters.tenantId, grupsTenantId)
          ) {
            return false;
          }
          return true;
        })
        .map((row) => ({
          ...row,
          tenantName: resolveRulesTenantDisplayName(
            row.tenantId,
            row.tenantName,
            tenantNameById
          ),
          eventTypeName: row.eventTypeName || "Todos los tipos",
          eventSubTypeName: row.eventSubTypeName || (row.eventTypeId == null ? "-" : "Todos"),
        })),
    [
      allRows,
      filters.eventSubTypeId,
      filters.eventTypeId,
      filters.tenantId,
      grupsTenantId,
      ownedTenantId,
      tenantNameById,
    ]
  );

  const [createRule, { isLoading: isCreating }] =
    useCreateEventAdvancedConfigVisibilityRuleMutation();
  const [updateRule, { isLoading: isUpdating }] =
    useUpdateEventAdvancedConfigVisibilityRuleMutation();
  const [toggleActive] = useToggleEventAdvancedConfigVisibilityRuleActiveMutation();
  const togglingRowIdsRef = useRef<Set<number>>(new Set());
  const isSubmitting = isCreating || isUpdating;
  const isEditScreen = formMode === "edit";

  useEffect(() => {
    onFormVisibilityChange?.(isEditScreen);
    return () => onFormVisibilityChange?.(false);
  }, [isEditScreen, onFormVisibilityChange]);

  useEffect(() => {
    if (tenantScope.loading || tenantScope.locked) {
      return;
    }

    setFormMode("hidden");
    setEditingRowId(null);
    setFormError(null);
  }, [tenantScope.loading, tenantScope.locked]);

  const openEditForm = (row: EventAdvancedConfigVisibilityRule) => {
    if (!tenantScope.locked) {
      return;
    }

    setFormMode("edit");
    setEditingRowId(row.id);
    setFormValues(eventAdvancedConfigTabConfig.mapRowToFormValues(row));
    setFormError(null);
  };

  const closeForm = () => {
    setFormMode("hidden");
    setEditingRowId(null);
    setFormError(null);
  };

  const lastCreateTriggerRef = useRef(createTrigger);
  useEffect(() => {
    if (createTrigger > lastCreateTriggerRef.current && tenantScope.locked) {
      setFormMode("create");
      setEditingRowId(null);
      setFormValues(
        applyOwnedRulesTenantId(eventAdvancedConfigTabConfig.getEmptyFormValues(), ownedTenantId)
      );
      setFormError(null);
    }
    lastCreateTriggerRef.current = createTrigger;
  }, [createTrigger, ownedTenantId, tenantScope.locked]);

  const handleToggleActive = async (row: EventAdvancedConfigVisibilityRule) => {
    if (!tenantScope.locked || togglingRowIdsRef.current.has(row.id)) {
      return;
    }

    togglingRowIdsRef.current.add(row.id);

    try {
      const result = await toggleActive(row.id).unwrap();
      toast.success(result.active ? "Regla activada" : "Regla desactivada");
    } catch (toggleError) {
      toast.error(
        getAdminFacingApiErrorMessage(
          toggleError,
          "No se pudo actualizar el estado de la regla."
        )
      );
    } finally {
      togglingRowIdsRef.current.delete(row.id);
    }
  };

  const handleSubmit = async () => {
    if (!tenantScope.locked) {
      return;
    }

    const submitValues = applyOwnedRulesTenantId(formValues, ownedTenantId);
    const validationError = eventAdvancedConfigTabConfig.validateForm?.(submitValues) ?? null;
    if (validationError) {
      setFormError(validationError);
      return;
    }

    if (isDuplicateRule(allRows, submitValues, editingRowId)) {
      setFormError(DUPLICATE_RULE_ERROR);
      return;
    }

    setFormError(null);

    try {
      if (formMode === "edit" && editingRowId != null) {
        await updateRule(
          eventAdvancedConfigTabConfig.mapFormToUpdatePayload?.(
            submitValues,
            editingRowId
          ) as Parameters<typeof updateRule>[0]
        ).unwrap();
        toast.success("Regla actualizada");
      } else {
        await createRule(
          eventAdvancedConfigTabConfig.mapFormToCreatePayload?.(
            submitValues
          ) as Parameters<typeof createRule>[0]
        ).unwrap();
        toast.success("Regla creada");
      }

      closeForm();
    } catch (submitError) {
      const status = getApiErrorStatus(submitError);
      const numericStatus =
        typeof status === "number" ? status : Number.parseInt(String(status ?? ""), 10);
      const isValidationLikeError =
        numericStatus === 400 || numericStatus === 409 || numericStatus === 404;
      const message = isValidationLikeError
        ? numericStatus === 409
          ? DUPLICATE_RULE_ERROR
          : getFormSubmitErrorMessage(
              submitError,
              "No se pudo guardar la regla. Revisá los datos ingresados."
            )
        : getAdminFacingApiErrorMessage(submitError, "No se pudo guardar la regla.");

      if (isValidationLikeError) {
        setFormError(message);
        toast(message, { icon: "⚠️" });
        return;
      }

      setFormError(message);
      toast.error(message);
    }
  };

  const formPanel = (
    <RulesInlineFormPanel<EventAdvancedConfigVisibilityRuleFormValues>
      mode={formMode === "hidden" ? "create" : formMode}
      values={formValues}
      onChange={setFormValues}
      customFields={eventAdvancedConfigTabConfig.inlineFormFields}
      scopeFields={eventAdvancedConfigTabConfig.scopeFields}
      onSubmit={handleSubmit}
      onCancel={closeForm}
      isSubmitting={isSubmitting}
      errorMessage={formError}
    />
  );

  if (isEditScreen) {
    return (
      <div className="flex flex-column gap-3">
        <Button
          label="Volver al listado"
          icon="pi pi-arrow-left"
          severity="secondary"
          text
          className="align-self-start"
          onClick={closeForm}
          disabled={isSubmitting}
        />
        {formPanel}
      </div>
    );
  }

  return (
    <div className="flex flex-column gap-3">
      {formMode === "create" && formPanel}

      {isError && (
        <div className="flex align-items-center justify-content-between p-3 border-round border-1 border-red-200 bg-red-50">
          <span className="text-red-600 font-medium">
            {getAdminFacingApiErrorMessage(error, RULES_LIST_LOAD_ERROR)}
          </span>
          <Button
            label="Reintentar"
            icon="pi pi-refresh"
            severity="danger"
            outlined
            size="small"
            onClick={() => refetch()}
          />
        </div>
      )}

      <RulesDataTable
        rows={rows}
        loading={isLoading && data === undefined}
        filters={filters}
        onFiltersChange={setFilters}
        customColumns={eventAdvancedConfigTabConfig.customColumns}
        onEdit={openEditForm}
        onToggleActive={handleToggleActive}
        mutationsDisabled={!tenantScope.locked}
      />
    </div>
  );
};
